import { t as localeText } from "@/shared/lib/translate";
import { track, type ProductGoal } from "@/shared/lib/analytics";
import { reportDiagnostic } from "@/shared/lib/diagnostics";
import createClient, { type Middleware } from "openapi-fetch";
import packageMetadata from "../../package.json";
import { env } from "@/shared/config/env";
import { openUpgradePrompt } from "@/shared/lib/upgradePrompt";
import { getCreditPlan, openCreditPaywall } from "@/shared/lib/creditAccess";
import type { paths } from "./contract";

/**
 * The typed API client — generated `paths` give full request/response typing.
 * Feature `api/` fns are the only callers; nothing else fetches.
 *
 * Auth: Afterform's session-token getter is injected once at app init via
 * `setTokenGetter` (see app/AuthBridge) and applied per-request by middleware,
 * so the client stays decoupled from React and the session implementation.
 */
type TokenGetter = (opts?: { skipCache?: boolean }) => Promise<string | null>;

let getToken: TokenGetter = async () => null;

export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

/**
 * Called when the API rejects our token (401). Wired by AuthBridge to refresh
 * the Afterform session and sign the user out (→ redirect to sign-in) if it's gone.
 */
let authErrorHandler: () => void = () => {};

export function setAuthErrorHandler(fn: () => void): void {
  authErrorHandler = fn;
}

/** Trigger the registered 401 recovery (token refresh / redirect to sign-in). */
export function notifyAuthError(): void {
  authErrorHandler();
}

/**
 * The current UI language (BCP-47), injected once at app init via
 * `setLanguageGetter` (see app/AuthBridge) so the client stays decoupled from
 * i18next. Sent as `Accept-Language` so the backend can localize responses
 * (e.g. model catalog descriptions). Defaults to English.
 */
let getLanguage: () => string = () => "en";

export function setLanguageGetter(fn: () => string): void {
  getLanguage = fn;
}

/**
 * Selected V2 ownership context. Returning null deliberately omits the header,
 * which makes the server select only the caller's personal Space.
 */
let getSpaceID: () => string | null = () => null;

export function currentSpaceID(): string { return getSpaceID()?.trim() ?? ""; }

export function setSpaceIDGetter(fn: () => string | null): void {
  getSpaceID = fn;
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ response }) {
    // A rejected token → kick off recovery (refresh, or redirect to sign-in).
    if (response.status === 401) notifyAuthError();
    return undefined;
  },
};

/**
 * Credit-consuming commands are gated before submission. Quotes, uploads,
 * downloads, local edits, and cancellation remain available without a plan.
 */
const creditActionRoutes = new Set([
  "/v1/generations",
  "/v1/generations/{id}/revisions",
  "/v1/generations/{id}/retry",
  "/v1/audio/documents/{id}/takes/generated",
  "/v1/audio/documents/{id}/generations/{generation_id}/retry",
]);
const creditRequestPlans = new WeakMap<Request, string>();

/**
 * Non-subscribers see the fullscreen paywall; subscribers who run out of
 * credits retain their upgrade recovery. Other 402
 * responses, such as storage limits, need their own recovery rather than a
 * misleading credit modal. The response body is read from a clone so the
 * original stream stays intact for the caller.
 */
const quotaMiddleware: Middleware = {
  async onRequest({ request, schemaPath }) {
    if (request.method !== "POST" || !creditActionRoutes.has(schemaPath)) return;
    const spaceID = request.headers.get("X-Space-ID") ?? currentSpaceID();
    let plan = getCreditPlan(spaceID);
    if (!plan) {
      // Cold cache or a just-switched workspace: resolve its payer before any
      // charged request. Do not use the balance snapshot, which waits for this
      // very submission to finish before reconciling optimistic deductions.
      const result = await api.GET("/v1/usage", {
        params: { header: { "X-Space-ID": spaceID || undefined } },
        signal: request.signal,
      });
      // Some team roles can create without viewing workspace billing. Leave
      // admission to the server when billing is unavailable to this actor.
      if (!result.response.ok) return;
      plan = result.data?.plan_id;
    }
    if (plan) creditRequestPlans.set(request, plan);
    if (plan !== "free") return;
    openCreditPaywall();
    return new Response(JSON.stringify({ error: {
      code: "insufficient_credit",
      get message() { return localeText("interface.choose_a_subscription_to_start_creating_02bde879", { defaultValue: "Choose a subscription to start creating." }); },
    } }), { status: 402, headers: { "Content-Type": "application/json" } });
  },
  async onResponse({ request, response }) {
    if (response.status === 402) {
      const body = (await response
        .clone()
        .json()
        .catch(() => null)) as { error?: { code?: string } } | null;
      if (
        body?.error?.code === "insufficient_credit" ||
        body?.error?.code === "quota_exceeded"
      ) {
        const plan = creditRequestPlans.get(request) ?? getCreditPlan(request.headers.get("X-Space-ID") ?? currentSpaceID());
        if (!plan || plan === "free") openCreditPaywall();
        else openUpgradePrompt();
      }
    }
    return undefined;
  },
};

/** Tell the backend the user's language so it can localize responses. */
const languageMiddleware: Middleware = {
  async onRequest({ request }) {
    request.headers.set("Accept-Language", getLanguage());
    return request;
  },
};

/** Frozen client telemetry and explicit team context for the V2 transport. */
const v2ContextMiddleware: Middleware = {
  async onRequest({ request }) {
    if (new URL(request.url).pathname.startsWith("/v1/")) {
      request.headers.set("X-Client-Platform", "web");
      request.headers.set("X-Client-Version", packageMetadata.version);
      request.headers.set("X-Client-API-Version", "v1");

      const spaceID = getSpaceID()?.trim();
      if (spaceID && !request.headers.has("X-Space-ID")) request.headers.set("X-Space-ID", spaceID);
    }
    return request;
  },
};

export const api = createClient<paths>({
  baseUrl: env.API_BASE_URL,
  // Resolve `fetch` per call (not bound at creation) so the global stays the one
  // source of truth — runtime uses the platform fetch, tests can stub it.
  fetch: (request) => fetch(request),
});
api.use(authMiddleware);
api.use(languageMiddleware);
api.use(v2ContextMiddleware);
api.use(quotaMiddleware);

const conversionRoutes: Record<string, ProductGoal> = {
  "/v1/projects": "project_created",
  "/v1/assets/uploads/{id}/complete": "asset_uploaded",
  "/v1/generations": "generation_submitted",
  "/v1/audio/documents/{id}/takes/generated": "generation_submitted",
  "/v1/billing/checkout": "checkout_started",
  "/v1/assets/{id}/shares": "share_created",
  "/v1/assets/bulk/download": "asset_download_requested",
};
const conversionKeys = new Set<string>();
api.use({
  onResponse({ request, response, schemaPath }) {
    if (response.status >= 500) {
      reportDiagnostic("api_error", response.headers.get("X-Request-ID") ?? "", response.status);
    }
    const goal = conversionRoutes[schemaPath];
    if (
      request.method !== "POST" || !response.ok || !goal ||
      response.headers.get("Idempotency-Replayed") === "true"
    ) return;
    const idempotencyKey = request.headers.get("Idempotency-Key");
    const key = idempotencyKey ? `${schemaPath}:${idempotencyKey}` : null;
    if (key && conversionKeys.has(key)) return;
    if (key) {
      conversionKeys.add(key);
      if (conversionKeys.size > 256) conversionKeys.delete(conversionKeys.values().next().value!);
    }
    try {
      track(goal);
    } catch { /* Analytics must not fail a product request. */ }
  },
});
